<template>
    <div ref="languageSelector" class="mobile-language-selector">
        <button class="mobile-language-toggle" @click="toggleDropdown">
            <span class="language-label">{{ t('navbar.language') }}:</span>
            <span class="current-language">{{ currentLanguageName }}</span>
            <svg
                class="dropdown-arrow"
                :class="{ open: mobileLanguageDropdownOpen }"
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M6 9L12 15L18 9"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
            </svg>
        </button>
        <div
            v-if="mobileLanguageDropdownOpen"
            class="mobile-language-dropdown-list"
        >
            <button
                v-for="lang in displayLanguages"
                :key="lang.code"
                @click="changeLanguage(lang.code)"
                class="mobile-language-item"
                :class="{ active: locale === lang.code }"
            >
                {{ lang.name }}
            </button>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { loadLocaleResources } from '@/i18n.js'
import { trackEvent } from '@/utils/tracking'

const props = defineProps({
    trackSource: {
        type: String,
        default: 'mobile_menu_language_selector',
    },
    /** When set, only these locale codes appear in the list (e.g. workshop: en + es). */
    allowedLocales: {
        type: Array,
        default: null,
    },
})

const { locale, t } = useI18n()
const languageSelector = ref(null)
const mobileLanguageDropdownOpen = ref(false)

const ALL_LANGUAGES = [
    { code: 'es', name: 'Español' },
    { code: 'en', name: 'English' },
    { code: 'pt', name: 'Português' },
    { code: 'de', name: 'Deutsch' },
    { code: 'nl', name: 'Nederlands' },
]

const displayLanguages = computed(() => {
    if (!props.allowedLocales?.length) {
        return ALL_LANGUAGES
    }
    const allowed = new Set(props.allowedLocales)
    return ALL_LANGUAGES.filter((lang) => allowed.has(lang.code))
})

const currentLanguageName = computed(() => {
    return (
        ALL_LANGUAGES.find((lang) => lang.code === locale.value)?.name ||
        'Español'
    )
})

const toggleDropdown = () => {
    mobileLanguageDropdownOpen.value = !mobileLanguageDropdownOpen.value
}

const changeLanguage = async (langCode) => {
    if (locale.value === langCode) {
        mobileLanguageDropdownOpen.value = false
        return
    }

    const previousLocale = locale.value

    try {
        const resolvedLocale = await loadLocaleResources(langCode)
        locale.value = resolvedLocale
        localStorage.setItem('preferredLanguage', resolvedLocale)
        mobileLanguageDropdownOpen.value = false

        trackEvent('Language Changed', {
            previous_language: previousLocale,
            new_language: resolvedLocale,
            page: window.location.pathname,
            source: props.trackSource,
        })
    } catch (error) {
        console.error('Failed to change language:', error)
    }
}

const handleClickOutside = (event) => {
    if (
        languageSelector.value &&
        !languageSelector.value.contains(event.target) &&
        mobileLanguageDropdownOpen.value
    ) {
        mobileLanguageDropdownOpen.value = false
    }
}

onMounted(() => {
    document.addEventListener('click', handleClickOutside)
})

onBeforeUnmount(() => {
    document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
.mobile-language-selector {
    padding: 16px 0;
    border-bottom: 1px solid rgba(201, 166, 70, 0.2);
    margin-bottom: 10px;
}

.mobile-language-toggle {
    width: 100%;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(201, 166, 70, 0.3);
    border-radius: 12px;
    padding: 14px 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: #f5f2e9;
    cursor: pointer;
    font-family: 'Nunito', sans-serif;
    font-size: 16px;
    transition: all 0.3s ease;
}

.mobile-language-toggle:active {
    background: rgba(201, 166, 70, 0.1);
}

.language-label {
    opacity: 0.7;
    font-size: 14px;
    margin-right: 8px;
}

.current-language {
    font-weight: 600;
    flex: 1;
    text-align: left;
}

.dropdown-arrow {
    transition: transform 0.3s ease;
}

.dropdown-arrow.open {
    transform: rotate(180deg);
}

.mobile-language-dropdown-list {
    margin-top: 8px;
    background: rgba(255, 255, 255, 0.03);
    border-radius: 12px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    animation: slideDown 0.3s ease;
}

.mobile-language-item {
    width: 100%;
    padding: 14px 20px;
    background: transparent;
    border: none;
    color: #f5f2e9;
    text-align: left;
    font-size: 15px;
    font-family: 'Nunito', sans-serif;
    cursor: pointer;
    transition: background 0.2s ease;
}

.mobile-language-item:active {
    background: rgba(201, 166, 70, 0.1);
}

.mobile-language-item.active {
    color: #c9a646;
    font-weight: 700;
    background: rgba(201, 166, 70, 0.05);
}

@keyframes slideDown {
    from {
        opacity: 0;
        transform: translateY(-10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}
</style>
